import { Box, SimpleGrid, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { fmtMoneyDebug, fmtIrrPct } from './calc/formatters';
import { resolveFacilitySize } from './calc/resolvePlanCovenantLimits';

function OutputRow({ label, value, format = 'money' }) {
  let display = '—';
  if (value != null && Number.isFinite(Number(value))) {
    if (format === 'money') {
      display = fmtMoneyDebug(value, 0);
    } else if (format === 'pct') {
      display = fmtIrrPct(Number(value));
    } else if (format === 'raw') {
      display = String(value);
    } else if (format === 'multiple') {
      display = `${Number(value).toFixed(2)}x`;
    } else {
      display = String(value);
    }
  }
  return (
    <Box>
      <Text fontSize="xs" color="gray.600" mb={0.5}>
        {label}
      </Text>
      <Text fontSize="sm" fontWeight="semibold">
        {display}
      </Text>
    </Box>
  );
}

OutputRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number,
  format: PropTypes.string,
};

function DealIrrModelInputsPanel({
  state,
  outputs,
  facilityFeeAmount,
  variant = 'both',
  spreadCalculated = false,
}) {
  const showInputs = variant === 'both' || variant === 'inputs';
  const showCalculated = variant === 'both' || variant === 'calculated';
  const gridColumns =
    variant === 'both' ? { base: 1, lg: 2 } : { base: 1 };
  const calculatedColumns = spreadCalculated
    ? { base: 2, sm: 3, md: 4, xl: 7 }
    : 2;
  const calculatedCompact = variant === 'calculated' && !spreadCalculated;

  return (
    <SimpleGrid
      columns={gridColumns}
      spacing={variant === 'both' ? 6 : 0}
      mb={variant === 'calculated' && !spreadCalculated ? 0 : 4}
      h={calculatedCompact ? '100%' : undefined}
    >
      {showInputs ? (
        <Box className="deal-irr-panel deal-irr-panel--inputs">
          <Text className="deal-irr-panel-title">Input</Text>
          <SimpleGrid columns={2} spacing={3}>
            <OutputRow label="Facility size" value={resolveFacilitySize(state)} />
            <OutputRow label="Facility fee (%)" value={state.facilityFeePct} format="raw" />
            <OutputRow label="Facility fee amount ($)" value={facilityFeeAmount} />
            <OutputRow
              label="Fee / XIRR %"
              value={state.feePct}
              format="raw"
            />
            <OutputRow
              label="Facility fee installments"
              value={state.facilityFeeInstallments ?? 1}
              format="raw"
            />
            <OutputRow label="Repayment grace (months)" value={state.gracePeriod} format="raw" />
            <OutputRow
              label="Marketing spend grace"
              value={state.marketingGracePeriod}
              format="raw"
            />
          </SimpleGrid>
        </Box>
      ) : null}
      {showCalculated ? (
        <Box
          className={`deal-irr-panel deal-irr-panel--outputs${
            calculatedCompact ? ' deal-irr-panel--compact' : ''
          }${spreadCalculated ? ' deal-irr-panel--spread' : ''}`}
        >
          <Text className="deal-irr-panel-title">Calculated</Text>
          <SimpleGrid
            columns={calculatedColumns}
            spacing={calculatedCompact ? 2 : 3}
          >
            <OutputRow label="Max Exposure calc ($)" value={outputs.maxExposureCalc} />
            <OutputRow label="Avg Exposure ($)" value={outputs.avgExposure} />
            <OutputRow label="Total Growth Investment" value={outputs.outflow} />
            <OutputRow label="Total Returns" value={outputs.inflow} />
            <OutputRow label="Total Facility Fee" value={outputs.totalFacilityFee} />
            <OutputRow label="IRR" value={outputs.irr} format="pct" />
            <OutputRow label="Net cash" value={outputs.netCash} />
          </SimpleGrid>
        </Box>
      ) : null}
    </SimpleGrid>
  );
}

DealIrrModelInputsPanel.propTypes = {
  state: PropTypes.object.isRequired,
  outputs: PropTypes.object.isRequired,
  facilityFeeAmount: PropTypes.number.isRequired,
  variant: PropTypes.oneOf(['both', 'inputs', 'calculated']),
  spreadCalculated: PropTypes.bool,
};

export default DealIrrModelInputsPanel;
